import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import { useSelector, useDispatch } from 'react-redux';
import { getTypeFromCategory, updatePinPosition } from '../../helpers/projectApi';
import { fetchPinData } from '../../hooks/useLoadPins';
import { setPinsByCategory, setEditingBoundary } from '../../../../../store/slices/projectItemSlice';
import { updatePinConnectedPoints } from '../../../../../store/slices/navigationSlice';
import { navSourceRef } from '../Navigation/NavigationLayer';
import { boundaryRef } from './BoundaryLayer';
import { FloorList } from '../../../mapData';
import { useMatch, useNavigate } from 'react-router-dom';
import useFlyToPin from '../../hooks/useFlyToPin'; 
import { useProjectHeader } from '../../../../../pages/buildProject/Helpers/pageDiv/ProjectHeaderContext';
import { encode } from '../../../../../helpers/utils';

const ICON_REGISTRY = {
  location: {
    width: 20, height: 25,
    svg: `<svg width="20" height="25" xmlns="http://www.w3.org/2000/svg" fill="none">
      <g>
        <path fill="{{color}}" d="m10,0c-5.53061,0 -10,4.32465 -10,9.67615c0,1.56005 0.42857,3.08055 1.08163,4.44315c0.46939,0.9873 1.55102,2.4684 1.81633,2.8238c1.53061,2.0142 7.10204,8.0569 7.10204,8.0569c0,0 5.5918,-6.0427 7.102,-8.0569c0.2653,-0.3554 1.347,-1.8167 1.8164,-2.8238c0.653,-1.3626 1.0816,-2.8831 1.0816,-4.44315c0,-5.3515 -4.4694,-9.67615 -10,-9.67615z"/>
        <path fill="white" d="m9.99998,17.7923c4.63242,0 8.38772,-3.6337 8.38772,-8.11613c0,-4.4824 -3.7553,-8.11611 -8.38772,-8.11611c-4.63242,0 -8.38775,3.63371 -8.38775,8.11611c0,4.48243 3.75533,8.11613 8.38775,8.11613z"/>
        <path fill="{{color}}" d="m10,16.331c3.7984,0 6.8776,-2.9795 6.8776,-6.65486c0,-3.67535 -3.0792,-6.65482 -6.8776,-6.65482c-3.79836,0 -6.87755,2.97947 -6.87755,6.65482c0,3.67536 3.07919,6.65486 6.87755,6.65486z"/>
        <path fill="white" d="m13.1429,7.80014c1.2623,0 2.2857,-0.99021 2.2857,-2.21169c0,-1.22148 -1.0234,-2.2117 -2.2857,-2.2117c-1.2624,0 -2.2858,0.99022 -2.2858,2.2117c0,1.22148 1.0234,2.21169 2.2858,2.21169z"/>
      </g>
    </svg>`,
  },
  product: {
    width: 20, height: 25,
    svg: `<svg width="20" height="25" xmlns="http://www.w3.org/2000/svg" fill="none">
      <g>
        <path fill="{{color}}" d="m10,0c-5.52783,0 -10,4.32764 -10,9.67682c0,1.56018 0.42227,3.08318 1.07486,4.43908c0.47985,1.003 1.5547,2.4703 1.82341,2.8232c1.51632,2.0245 7.10173,8.0609 7.10173,8.0609c0,0 5.5854,-6.0364 7.1017,-8.0609c0.2687,-0.3529 1.3436,-1.8202 1.8234,-2.8232c0.6526,-1.3559 1.0749,-2.8789 1.0749,-4.43908c0,-5.34918 -4.4722,-9.67682 -10,-9.67682z"/>
        <path fill="white" d="m9.99999,17.7935c4.63241,0 8.38771,-3.634 8.38771,-8.11669c0,-4.48269 -3.7553,-8.11664 -8.38771,-8.11664c-4.63241,0 -8.38772,3.63395 -8.38772,8.11664c0,4.48269 3.75531,8.11669 8.38772,8.11669z"/>
        <path fill="{{color}}" d="m10,16.3262c3.795,0 6.8714,-2.9771 6.8714,-6.64938c0,-3.67232 -3.0764,-6.64933 -6.8714,-6.64933c-3.79497,0 -6.8714,2.97701 -6.8714,6.64933c0,3.67228 3.07643,6.64938 6.8714,6.64938z"/>
        <path fill="white" d="m10,11.9057c1.2721,0 2.3033,-0.9979 2.3033,-2.22888c0,-1.23094 -1.0312,-2.22882 -2.3033,-2.22882c-1.27206,0 -2.30327,0.99788 -2.30327,2.22882c0,1.23098 1.03121,2.22888 2.30327,2.22888z"/>
      </g>
    </svg>`,
  },
  beacon: {
    width: 18, height: 18,
    svg: `<svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" fill="none">
      <g>
        <path fill="{{color}}" d="m9.95319,0.04415c-0.31035,-0.02209 -0.62066,-0.04415 -0.931,-0.04415c-0.93104,0 -1.83988,0.1325 -2.68225,0.39753c-0.84236,0.26503 -1.64041,0.64051 -2.37194,1.1264c-2.39409,1.61227 -3.968,4.3509 -3.968,7.46502c0,0.35337 0.02216,0.70674 0.0665,1.06015c0,0 0,0.0442 0,0.0662c0.04434,0.3313 0.11088,0.6847 0.17738,1.016c0.0665,0.2871 0.15516,0.5963 0.266,0.8834c0.04433,0.1105 0.08866,0.243 0.133,0.3534c0.02216,0.0662 0.06652,0.1325 0.08868,0.2209c1.39656,3.1803 4.56647,5.411 8.26844,5.411c3.702,0 6.8941,-2.2307 8.2685,-5.411c0.0222,-0.0663 0.0665,-0.1326 0.0887,-0.2209c0.0443,-0.1104 0.0887,-0.2429 0.133,-0.3534c0.0887,-0.2871 0.1995,-0.5742 0.266,-0.8834c0.0887,-0.3313 0.133,-0.6626 0.1773,-0.9939c0,0 0,-0.0441 0,-0.0662c0.0443,-0.35341 0.0665,-0.70677 0.0665,-1.06015c0,-0.3092 0,-0.61843 -0.0443,-0.92763c-0.4212,-4.2405 -3.8128,-7.59753 -8.04682,-8.01717z"/>
        <path fill="white" d="m9.02202,16.5206c4.17478,0 7.55908,-3.3719 7.55908,-7.53129c0,-4.15942 -3.3843,-7.5313 -7.55908,-7.5313c-4.17479,0 -7.55913,3.37188 -7.55913,7.5313c0,4.15939 3.38434,7.53129 7.55913,7.53129z"/>
        <path fill="{{color}}" d="m9.0223,15.1733c3.428,0 6.2069,-2.7687 6.2069,-6.18406c0,-3.41536 -2.7789,-6.18406 -6.2069,-6.18406c-3.42797,0 -6.20687,2.7687 -6.20687,6.18406c0,3.41536 2.7789,6.18406 6.20687,6.18406z"/>
        <path fill="white" d="m11.8594,7.24421c1.1386,0 2.0616,-0.91962 2.0616,-2.05401c0,-1.13438 -0.923,-2.05397 -2.0616,-2.05397c-1.1386,0 -2.06155,0.91959 -2.06155,2.05397c0,1.13439 0.92295,2.05401 2.06155,2.05401z"/>
      </g>
    </svg>`, 
  },
  amenity: {
    width: 20, height: 20,
    svg: `<svg width="20" height="20" xmlns="http://www.w3.org/2000/svg" fill="none">
      <g>
        <path fill="{{color}}" d="m10,20c5.5228,0 10,-4.4772 10,-10c0,-5.52285 -4.4772,-10 -10,-10c-5.52285,0 -10,4.47715 -10,10c0,5.5228 4.47715,10 10,10z"/>
        <path fill="#FAFAFA" d="m10.0001,5.1566c0.668,0 1.2108,-0.54278 1.2108,-1.21087c0,-0.66809 -0.5428,-1.21087 -1.2108,-1.21087c-0.66806,0 -1.21088,0.54278 -1.21088,1.21087c0,0.66809 0.54282,1.21087 1.21088,1.21087zm1.2526,0.27141l-2.54701,0c-0.85596,0 -1.52405,0.68894 -1.52405,1.56575l0,3.69524c0,0.7098 1.04386,0.7098 1.04386,0l0,-3.3821l0.25052,0l0,9.2276c0,0.9603 1.39876,0.9394 1.39876,0l0,-5.3654l0.25052,0l0,5.3654c0,0.9394 1.3988,0.9603 1.3988,0l0,-9.2276l0.2505,0l0,3.3821c0,0.7306 1.0438,0.7306 1.0438,0l0,-3.67436c0,-0.79333 -0.6262,-1.58663 -1.5448,-1.58663z"/>
      </g>
    </svg>`,
  },
  safety: {
    width: 20, height: 20,
    svg: `<svg width="20" height="20" xmlns="http://www.w3.org/2000/svg" fill="none">
      <g>
        <path fill="{{color}}" d="m17.9431,0l-15.88619,0c-1.136,0 -2.05691,0.92091 -2.05691,2.05691l0,15.88619c0,1.136 0.92091,2.0569 2.05691,2.0569l15.88619,0c1.136,0 2.0569,-0.9209 2.0569,-2.0569l0,-15.88619c0,-1.136 -0.9209,-2.05691 -2.0569,-2.05691z"/>
        <path fill="white" d="m7.56082,4.602l-0.47695,0l0,-1.08166l0.87521,0l0.43767,-0.43767l0,-0.65644l-0.43767,-0.43767l-1.09411,0l0,-0.43753l-1.53174,0l0,0.43753l-0.65644,0c-2.84459,1.53178 -3.28226,2.84475 -3.28226,2.84475l0,0.43754l0.65644,0l0,-0.43754c0,0 1.09411,-1.31297 2.18818,-1.53174l0.87534,0l0,1.30043l-0.49562,0c-0.87948,0 -1.59238,0.71306 -1.59238,1.59251l0,12.20539l6.12687,0l0,-12.20536c0,-0.87948 -0.7131,-1.59254 -1.59254,-1.59254z"/>
      </g>
    </svg>`,
  },
  vertical_transport: {
    width: 20, height: 20,
    svg: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none">
      <g>
        <path fill="{{color}}" d="m10,20c5.5228,0 10,-4.4772 10,-10c0,-5.52285 -4.4772,-10 -10,-10c-5.52285,0 -10,4.47715 -10,10c0,5.5228 4.47715,10 10,10z"/>
        <path fill="white" d="m16.5959,15.1063l0,-1.4893l-2.9787,0l0,-2.9788l-2.7659,0l0,-2.97868l-2.97877,0l0,-2.76597l-4.25534,0l0,1.70213l2.766,0l0,2.76597l2.76594,0l0,2.97875l2.97867,0l0,2.7659l4.4681,0z"/>
      </g>
    </svg>`,
  },
  vertical: {
    width: 20, height: 20,
    svg: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none">
      <g>
        <path fill="{{color}}" d="m10,20c5.5228,0 10,-4.4772 10,-10c0,-5.52285 -4.4772,-10 -10,-10c-5.52285,0 -10,4.47715 -10,10c0,5.5228 4.47715,10 10,10z"/>
        <path fill="white" d="m16.5959,15.1063l0,-1.4893l-2.9787,0l0,-2.9788l-2.7659,0l0,-2.97868l-2.97877,0l0,-2.76597l-4.25534,0l0,1.70213l2.766,0l0,2.76597l2.76594,0l0,2.97875l2.97867,0l0,2.7659l4.4681,0z"/>
      </g>
    </svg>`,
  },
}; 

const ICON_URL_CACHE = Object.fromEntries(
  Object.entries(ICON_REGISTRY).map(([category, { svg }]) => [
    category,
    `url(data:image/svg+xml;base64,${btoa(svg)})`,
  ])
);

// const getIconUrl = (category) => ICON_URL_CACHE[category] ?? ICON_URL_CACHE.location;
const iconCache = new Map();

const getIconUrl = (category, color = "#26A3DB", path = undefined) => {
  if(path){ 
    const svg = path.replace(/#374046/g, color).replace(/#6A6D73/g, color);
    const url = `url(data:image/svg+xml;base64,${btoa(svg)})`;
    return url
  }
  const key = `${category}-${color}`;
  if (iconCache.has(key)) return iconCache.get(key);
  
  const icon = ICON_REGISTRY[category] ?? ICON_REGISTRY.location;
  const svg = icon.svg.replace(/{{color}}/g, color);
  
  const url = `url(data:image/svg+xml;base64,${btoa(svg)})`;
  
  iconCache.set(key, url);
  return url;
};

const parsePosition = (positions) => {
  if (!positions) return null;
  try {
    const parsed = typeof positions === 'string' ? JSON.parse(positions) : positions;
    const x = Number(parsed.x);
    const y = Number(parsed.y);
    if (isNaN(x) || isNaN(y)) return null;
    return { x, y };
  } catch {
    return null;
  }
};

const createMarkerElement = ({ category, subType, title, isEditing, color, path }) => { 
  const wrapper = document.createElement('div');
  // wrapper.className = `mm-wrapper${isEditing ? ' mm-wrapper--editing' : ''}`;
  wrapper.className = `mm-wrapper`;
  
  const icon = document.createElement('div');
  // icon.className = `mm-icon marker-pop ${category} ${subType || ''}`;
  icon.className = `mm-icon ${category} ${subType || ''} icon-${category}`;
  if (isEditing) icon.classList.add('marker-wobble');
  if (isEditing) icon.classList.add('mm-wrapper--editing');
  icon.style.backgroundImage = getIconUrl(category, color, path);
 
  // if(category == 'location'){
  //   icon.classList.add('icon-location')
  // }else if(category == 'vertical'){ 
  // }else if(category == 'beacon'){ 
  // }else if(category == 'amenity'){ 
  // }else if(category == 'safety'){ 
  // }else if(category == 'product'){ 
  // }
  
  
  
  if (title) {
    const label = document.createElement('div');
    label.className = 'mm-label mm-label-show';
    label.textContent = title;
    label.classList.toggle('mm-label-show', !isEditing);
    
    wrapper.appendChild(label);
  } 
  
  wrapper.appendChild(icon);
  return wrapper;
};

const MapMarkers = React.memo(() => {
  const map                     = useSelector((s) => s.map.mapContainer);
  const activeTab               = useSelector((s) => s.api.activeTab);
  const allPins                 = useSelector((s) => s.api.allPins);
  const editingPinId            = useSelector((s) => s.api.editingPinId);
  const currentFloor            = useSelector((s) => s.api.currentFloor);
  const projectData             = useSelector((s) => s.api.projectData);
  const editingBoundary         = useSelector((s) => s.api.editingBoundary);
  const { handleEnableDisable, setItemMoved } = useProjectHeader();
  const isDrawing               = useSelector((s) => !!s.drawingToolbar.activeTool);
  const isSelectTool            = useSelector((s) => s.navigation.activeTool === 'select');
  const navigate                = useNavigate();
  const isNavigation            = !!useMatch('/project/:id/navigation'); 
  const isLocationRoute         = !!useMatch('/project/:id/location');
  const isProductRoute          = !!useMatch('/project/:id/product');
  const isBeaconRoute           = !!useMatch('/project/:id/beacon');
  const isAmenityRoute          = !!useMatch('/project/:id/amenity');
  const isSafetyRoute           = !!useMatch('/project/:id/safety');
  const isVerticalRoute         = !!useMatch('/project/:id/vertical-transport');
  
  const isSidebarRoute          = isLocationRoute || isProductRoute || isBeaconRoute || isAmenityRoute || isSafetyRoute  || isVerticalRoute;
  
  const dispatch     = useDispatch();
  const flyToPin     = useFlyToPin();
  // const { subid }    = useParams();
  
  const paths           = useSelector((s) => s.navigation.paths);
  const selectedPathId  = useSelector((s) => s.navigation.selectedPathId);
  const selectedPointId = useSelector((s) => s.navigation.selectedPointId);
  const shortestPath    = useSelector((s) => s.navigation.shortestPath);
  
  const isSelectToolRef      = useRef(isSelectTool);
  const isNavigationRef      = useRef(isNavigation);
  const pathsRef             = useRef(paths);
  const selectedPathIdRef    = useRef(selectedPathId);
  const selectedPointIdRef   = useRef(selectedPointId);
  const currentFloorRef      = useRef(currentFloor);
  const isSidebarRouteRef    = useRef(isSidebarRoute);
  const editingBoundaryRef   = useRef(editingBoundary);
  isSidebarRouteRef.current  = isSidebarRoute;
  isSelectToolRef.current    = isSelectTool;
  isNavigationRef.current    = isNavigation;
  pathsRef.current           = paths;
  selectedPathIdRef.current  = selectedPathId;
  selectedPointIdRef.current = selectedPointId;
  currentFloorRef.current    = currentFloor;
  editingBoundaryRef.current = editingBoundary;
  
  const markerRegistryRef = useRef(new Map());
  
  useEffect(() => {
    markerRegistryRef.current.forEach((marker) => {
      marker.setDraggable(isNavigation ? isSelectTool : true);
    });
  }, [isSelectTool,isNavigation]);
  
  const visiblePins = useMemo(() => {
    if (isDrawing) return [];
    
    const { vertical, ...restPins } = allPins || {};
    const floorId = currentFloor?.enc_id; 
    
    let regularPins = Object.values(restPins).flat().filter((pin) => {
      if (!pin?.positions) return false;
      if (pin?.fp_id !== floorId) return false;
      if (activeTab !== "all" && pin?.category !== activeTab) return false;
      return true;
    });
    
    regularPins = (regularPins || []).flatMap((pin) => { 
      return [{ ...pin, store_Id:`${pin.enc_id}_${pin.title}_${pin.positions}`}];
    });   
    
    const verticalPins = (vertical || []).flatMap((pin) => {
      if (activeTab !== "all" && activeTab !== "vertical") return [];
      if (!Array.isArray(pin.connections) || !floorId) return [];
      const conn = pin.connections.find((c) => c.fp_id === floorId);
      if (!conn?.positions) return []; 
      let { x,y } = JSON.parse(conn.positions)
      // `${pin.enc_id}_${conn.fp_id}_${pin.title}_${conn.positions}`
      return [{ ...pin, store_Id:`${pin.enc_id}_${conn.fp_id}_${pin.title}_${x}_${y}`, positions: JSON.parse(conn.positions) }];
    });  
    

    return [...regularPins, ...verticalPins];
  }, [allPins, activeTab, currentFloor, isDrawing]); 
  
  const handleDragEnd = useCallback(
    async (marker, feature) => {
      
      if (isNavigationRef.current && !isSelectToolRef.current) return;
      
      const { lng, lat } = marker.getLngLat(); 
      const { category, enc_id } = feature;
      
      const floorId = currentFloor?.enc_id;
      dispatch(updatePinConnectedPoints({ pinId: enc_id, position: [lng, lat], floorId }));
      
      const payload = {
        project_id: projectData?.enc_id,
        id: enc_id,
        fp_id: floorId,
        type: getTypeFromCategory(category),
        positions: { x: lng, y: lat },
        is_published: '0',
        discard: '1',
        publish: '1',
      };  

      
      
      if (category === 'location') { 
        const liveCorners = boundaryRef.getCornersForPin?.(enc_id);
        setItemMoved({ x: lng, y: lat })

        if (liveCorners) {
          payload.boundary_attributes = { corners: liveCorners };
        } else if (feature?.boundary_attributes && feature.boundary_attributes !== 'null') { 
          try {
            let parsedAttrs = typeof feature.boundary_attributes === 'string'
              ? JSON.parse(feature.boundary_attributes)
              : feature.boundary_attributes;
            if (typeof parsedAttrs === 'string') parsedAttrs = JSON.parse(parsedAttrs);

            if (parsedAttrs?.corners && Array.isArray(parsedAttrs.corners)) {
              const oldPos = parsePosition(feature.positions);
              const dlng = oldPos ? lng - oldPos.x : 0;
              const dlat = oldPos ? lat - oldPos.y : 0;
              payload.boundary_attributes = {
                ...parsedAttrs,
                corners: parsedAttrs.corners.map(([cx, cy]) => [cx + dlng, cy + dlat]),
              };
            } else {
              payload.boundary_attributes = parsedAttrs;
            }
          } catch {
            payload.boundary_attributes = null;
          }
        } else {
          payload.boundary_attributes = null;
        }

      }
      
      try {
        const result = await updatePinPosition(payload);
        if (result?.type) { 
          if (category === 'location' && payload.boundary_attributes?.corners) {
            const prevBoundary = editingBoundaryRef.current;
            if (prevBoundary) {
              dispatch(setEditingBoundary({
                color:      prevBoundary.color,
                attributes: { corners: payload.boundary_attributes.corners },
              }));
            }
          }

          const refetched = await fetchPinData(projectData?.enc_id, [category]);
          if (refetched?.[category]) {
            dispatch(setPinsByCategory({ [category]: refetched[category] }));
          }
          handleEnableDisable();
        }
      } catch (err) {
        console.error('[MapMarkers] Failed to update pin position:', err);
      }
    },
    [projectData, currentFloor, dispatch]
  );
  
  const bringToFront = useCallback((targetMarker) => {
    markerRegistryRef.current.forEach((m) => {
      m.getElement().style.zIndex = '1000';
    });
    targetMarker.getElement().style.zIndex = '2000';
  }, []);
  
  useEffect(() => {
    if (!map) return; 
    
    const registry   = markerRegistryRef.current; 
    const incomingIds = new Set(visiblePins.map((p) => p.store_Id));
    
    
    registry.forEach((marker, id) => {
      if (!incomingIds.has(id)) {
        marker.remove();
        registry.delete(id);
      }
    });
    
    
    visiblePins.forEach((feature) => {
      const { enc_id, store_Id, category, subType, title, positions, path } = feature;
      
      const { amenity_color, beacon_color, product_color, safety_color, location_color } = projectData;
      let color = "#26A3DB";
      if      (category === 'location')  color = feature.location_color ?? location_color;
      else if (category === 'beacon')    color = feature.beacon_color   ?? beacon_color;
      else if (category === 'amenity')   color = feature.amenity_color  ?? amenity_color;
      else if (category === 'safety')    color = feature.safety_color   ?? safety_color;
      else if (category === 'product')   color = feature.product_color  ?? product_color;
      else if (category === 'vertical')  color = feature.vt_color       ?? "#374046";
       
      if (registry.has(store_Id)) {
        const existing  = registry.get(store_Id);
        const wrapperEl = existing.getElement();
        const iconEl    = wrapperEl.querySelector('.mm-icon');
        const isEditing = editingPinId === enc_id;
        if (iconEl) {
          iconEl.classList.toggle('mm-wrapper--editing', isEditing);
          iconEl.classList.toggle('marker-wobble',       isEditing);
          
          iconEl.style.backgroundImage = getIconUrl(category, color, path);
        }
        const label = wrapperEl.querySelector('.mm-label');
        if (label) {
          label.classList.toggle('mm-label-show', !isEditing);
        }
        return;
      }
      
      const pos = parsePosition(positions);
      
      if (!pos) {
        console.warn(`Invalid position for pin ${enc_id}:`, positions);
        return;
      }
      
      const isEditing = editingPinId === enc_id;
      
      if(isEditing){
        flyToPin(pos)
      }
      
      const element   = createMarkerElement({ category, subType, title, isEditing, color, path }); 
 
      let marker;
      try {
        marker = new maplibregl.Marker({
          element, 
          draggable: shortestPath?.positions ? false : isNavigation ? isSelectToolRef.current : true,
        })
        .setLngLat([pos.x, pos.y])
        .addTo(map);
      } catch (err) {
        console.error(`Failed to create marker for ${enc_id}:`, err);
        return;
      }
      
      element.addEventListener('click', (e) => {
        // e.stopPropagation(); 
        bringToFront(marker);    
      });
      
      element.addEventListener('dblclick', (e) => { 
        e.stopPropagation();
        // if (!enc_id) return;

        if (!enc_id || !isSidebarRouteRef.current) return;
        
        
        const encoded = encode(enc_id);  
        const projectID = encode(projectData?.enc_id);  
        
        
        if (category === 'location')  navigate(`/project/${projectID}/location/${encoded}`);
        else if (category === 'product')  navigate(`/project/${projectID}/product/${encoded}`);
        else if (category === 'beacon')   navigate(`/project/${projectID}/beacon/${encoded}`);
        else if (category === 'amenity')  navigate(`/project/${projectID}/amenity/${encoded}`);
        else if (category === 'safety')   navigate(`/project/${projectID}/safety/${encoded}`);
        else if (category === 'vertical') navigate(`/project/${projectID}/vertical-transport/${encoded}`);
      });
      
      marker.on('dragstart', () => bringToFront(marker));
      
      marker.on('drag', () => {
        // if (isNavigation && !isSelectToolRef.current) return;
        // if (!isSelectToolRef.current) return;
        let { lng, lat } = marker.getLngLat();

        if (boundaryRef.moveTo) {
          const result = boundaryRef.moveTo(enc_id, lng, lat);
          if (result && result.ok === false) {
            marker.setLngLat([result.lng, result.lat]);
            lng = result.lng;
            lat = result.lat;
          }
        }

        const newPos = [lng, lat];

        const floorId  = currentFloorRef.current?.enc_id ?? null;
        const curPaths = pathsRef.current.filter((p) => p.floorId === floorId);

        const updatedPaths = curPaths.map((path) => {
          const hasPinPt = path.points.some((pt) => pt.pinId === enc_id);
          if (!hasPinPt) return path;
          return {
            ...path,
            points: path.points.map((pt) =>
              pt.pinId === enc_id ? { ...pt, position: newPos } : pt
          )};
        });

        navSourceRef.setLines?.(updatedPaths, selectedPathIdRef.current);
        navSourceRef.setNodes?.(updatedPaths, selectedPathIdRef.current, selectedPointIdRef.current);
      });
      
      marker.on('dragend', () => handleDragEnd(marker, feature));
      
      registry.set(store_Id, marker);
    });
    
  }, [map, visiblePins, editingPinId, isNavigation, shortestPath, bringToFront, handleDragEnd]);
  
  useEffect(() => {
    const registry = markerRegistryRef.current;
    return () => {
      registry.forEach((marker) => {
        try { marker.remove(); } catch {}
      });
      registry.clear();
    };
  }, [map]);
  
  return null;
}); 

MapMarkers.displayName = 'MapMarkers';

export default MapMarkers;